import isEqual from 'lodash.isequal';
import { isNonRetryableError } from '@rsos/sinatra/src/utils/createAccessTokenProvider';
import { captureExceptionWithScope } from '@rsos/utils/sentry';

function _requiredParameter() {
  throw new Error('Missing required parameter.');
}

const _DEV_HOST = 'wss://api-dev.rapidsos.com';
const _WS_STATE_CONNECTING = 0;
const _WS_STATE_OPEN = 1;
const _WS_STATE_CLOSED = 3;
const _SUBSCRIPTION_ACTION = 'subscription';
const _SUBSCRIPTION_ACTION_SUBSCRIBE = 'subscribe';
const _SUBSCRIPTION_ACTION_UNSUBSCRIBE = 'unsubscribe';
const _MAX_RECONNECTIONS = 20;
const _RECONNECTION_DELAY = 1000;

// Backoff bounds. Without a floor the jittered delay lands on 0 ms often
// enough to burn the whole reconnection budget inside a second.
const _RECONNECTION_DELAY_FLOOR = 250;
const _RECONNECTION_DELAY_CAP = 30000;

// Staleness watchdog. The server pings at the protocol level every 30s
// (mco_framework_async sets websocket_ping_interval), but the browser answers
// those in its network stack and the JS WebSocket API cannot observe them, so
// inbound application messages are the only liveness signal available here.
const _STALE_CHECK_INTERVAL = 20000;
const _STALE_INBOUND_LIMIT = 60000;

// The websocket server handles only the `subscription` message type and drops
// every other type without a reply or a log line, which makes this a probe with
// no server-side cost and no side effects.
const _PROBE_MESSAGE_TYPE = 'ping';

/**
 * Reads `navigator.onLine`, or undefined where there is no navigator. Only ever
 * a hint: it reports the machine has *a* network, not that Hermes is reachable,
 * and it is wrong on captive portals and on associated-but-dead WiFi. Never
 * gate a connection attempt on it.
 * @returns {boolean|undefined}
 */
function _onlineHint() {
  return typeof navigator === 'undefined' ? undefined : navigator.onLine;
}

/**
 * @returns {string|undefined} `document.visibilityState`, or undefined outside
 * a browser.
 */
function _visibilityState() {
  return typeof document === 'undefined' ? undefined : document.visibilityState;
}

/**
 * Returns a full-jitter backoff delay, bounded below by a floor so a burst of
 * reconnects cannot fire back-to-back, and above by a cap.
 * @param {number} attempt - Reconnection attempt number
 * @param {number} delay - Base delay in ms
 * @returns {number} Delay in ms
 */
export function getReconnectionDelay(attempt, delay) {
  const jitterWindow = Math.min(
    Math.pow(2, attempt) * delay,
    _RECONNECTION_DELAY_CAP,
  );
  return Math.max(
    _RECONNECTION_DELAY_FLOOR,
    Math.floor(Math.random() * jitterWindow),
  );
}

/**
 * Shared connection and reconnection lifecycle for the Hermes clients.
 * Subclasses supply the connection URL; everything else lives here so a fix
 * lands once for both the `/v1/stream` and `/hermes/connect` clients.
 *
 * This class holds no tokens. Every connection attempt awaits
 * `config.getAccessToken()`, so storage, expiry and refresh belong entirely to
 * whoever supplies that function.
 *
 * Exhaustion is recoverable rather than terminal: the client listens for the
 * browser coming back online and for its tab becoming visible, and either one
 * restores the full budget and retries. `closeConnection()` is the only way to
 * end it for good.
 */
class HermesClientBase {
  /**
   * @param {Object} config - An object consisting of configuration options
   * @param {function} config.getAccessToken - `() => Promise<string>`, awaited
   *   before every connection attempt. Required.
   * @param {string} [config.host='wss://api-dev.rapidsos.com'] - Hermes host environment to connect to
   * @param {number} [config.reconnectionDelay=1000] - Base delay when reconnecting
   * @param {number} [config.maxReconnections=20] - Max # of times to attempt
   *   reconnection before giving up, until a wake trigger restores the budget.
   * @param {string} [config.irpVersion] - IRP version number
   * @param {boolean} [config.hasHermesV2Access] - Whether this client may use Hermes v2
   * @param {string} [config.organizationID] - Organization ID
   * @param {string} [config.applicationName] - Application name
   * @param {number} [config.staleCheckInterval] - Staleness watchdog tick, ms
   * @param {number} [config.staleInboundLimit] - Inbound silence tolerated before probing, ms
   */
  constructor(config = {}) {
    if (typeof config.getAccessToken !== 'function') {
      _requiredParameter();
    }

    // Set config-based instance attributes
    this._getAccessToken = config.getAccessToken;
    this._ws_host = config.host || _DEV_HOST;
    this._reconnectionDelay = config.reconnectionDelay || _RECONNECTION_DELAY;
    this._maxReconnections = config.maxReconnections || _MAX_RECONNECTIONS;
    this._irpVersion = config.irpVersion;
    this.hasHermesV2Access = config.hasHermesV2Access;
    this.organization_id = config.organizationID;
    this.application_name = config.applicationName;
    this._staleCheckInterval =
      config.staleCheckInterval || _STALE_CHECK_INTERVAL;
    this._staleInboundLimit = config.staleInboundLimit || _STALE_INBOUND_LIMIT;

    // Identifies which of the two clients a Sentry report came from.
    // Subclasses overwrite this.
    this._client = 'hermes';

    // Initialize resources to be modifed for connection
    this._topics = new Set();
    this._callback = undefined;
    this._callbacks = {};
    this._reconnectCallbacks = {};
    this._closeCallbacks = {};
    this._connection = undefined;

    // Two fields, deliberately not one. `_reconnectionCount` is the budget
    // spent so far, and a wake trigger hands the whole budget back by zeroing
    // it. `_isReconnection` records that the socket about to open is replacing
    // one that died, which stays true across that reset — it is what
    // `_reconnectCallbacks` subscribers use to re-sync after an outage, and
    // deriving it from the count instead would go false the moment a wake
    // trigger fired and leave those subscribers holding stale state.
    this._reconnectionCount = 0;
    this._isReconnection = false;

    // Set while a connection attempt is awaiting its token, so concurrent
    // subscribe() calls do not each start their own socket.
    this._opening = false;

    // Set by closeConnection() so that neither an attempt already awaiting its
    // token nor a reconnect already scheduled can open a socket after the
    // caller asked this client to stop. subscribe() clears it.
    this._stopped = false;
    this._reconnectTimer = undefined;

    // Staleness watchdog state
    this._staleTimer = undefined;
    this._lastInboundAt = undefined;
    this._probeBufferedAmount = undefined;

    // Exhaustion diagnostics: when this run of failures started, and what the
    // last socket close looked like.
    this._firstFailureAt = undefined;
    this._lastCloseEvent = {};

    // Bound once so the same references can be removed again.
    this._onWake = () => this._handleWakeTrigger();
    this._onVisibilityChange = () => {
      if (_visibilityState() === 'visible') {
        this._handleWakeTrigger();
      }
    };
    this._wakeListenersAttached = false;
  }

  /**
   * Subscribes to the events that mean a previously impossible connection may
   * now be possible. Idempotent, and a no-op outside a browser.
   * @private
   */
  _addWakeListeners() {
    if (this._wakeListenersAttached || typeof window === 'undefined') {
      return;
    }
    window.addEventListener('online', this._onWake);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }
    this._wakeListenersAttached = true;
  }

  /**
   * Detaches the wake listeners, so a client nothing holds any more leaves
   * nothing behind on window or document.
   * @private
   */
  _removeWakeListeners() {
    if (!this._wakeListenersAttached || typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('online', this._onWake);
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this._onVisibilityChange,
      );
    }
    this._wakeListenersAttached = false;
  }

  /**
   * Comes back from the network or the tab becoming usable again. Restores the
   * full budget and retries at once, deliberately including the exhausted
   * state: without this the only way back is a page reload, and a dispatcher
   * has no reason to know that is what their blank panel needs.
   * @private
   */
  _handleWakeTrigger() {
    if (this._stopped || this._opening) {
      return;
    }

    const readyState = this._connection && this._connection.readyState;
    if (readyState === _WS_STATE_OPEN || readyState === _WS_STATE_CONNECTING) {
      return;
    }

    if (this._reconnectTimer !== undefined) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }

    this._isReconnection = true;
    this._resetReconnectionBudget();
    this._openConnection();
  }

  /**
   * Returns the client to the state it is in before any failure.
   * @private
   */
  _resetReconnectionBudget() {
    // `_isReconnection` is deliberately left alone. A wake trigger calls this
    // and then reopens, and the socket it opens is still replacing a dead one.
    this._reconnectionCount = 0;
    this._firstFailureAt = undefined;
  }

  /**
   * Whether this client has spent its reconnection budget.
   * @returns {boolean}
   * @private
   */
  _isExhausted() {
    return this._reconnectionCount >= this._maxReconnections;
  }

  /**
   * Path this client connects on, e.g. `/v1/stream`. Subclasses must implement
   * it. A getter rather than a constructor field so it can depend on state
   * that changes after construction.
   * @returns {string}
   * @private
   */
  get _connectPath() {
    throw new Error(
      `${this._client} must implement _connectPath before connecting.`,
    );
  }

  /**
   * Query string of any client-specific params, in `a=b&c=d` form. Supplied by
   * the meta tags, which is why it arrives as a string rather than an object.
   * @returns {string}
   * @private
   */
  get _extraQueryParams() {
    return '';
  }

  /**
   * Builds the WebSocket URL for this client. The params are the same for both
   * clients; only the path and the meta tag supplying the extras differ. An
   * extra sharing a key with one of the params below replaces it rather than
   * appending a second copy of that key.
   * @param {string} accessToken - Token resolved for this connection attempt
   * @returns {string}
   * @private
   */
  _buildConnectionUrl(accessToken) {
    const url = new URL(`${this._ws_host}${this._connectPath}`);
    url.search = new URLSearchParams({
      access_token: accessToken,
      irpVersion: this._irpVersion,
      organization_id: this.organization_id,
      application_name: this.application_name,
      ...Object.fromEntries(new URLSearchParams(this._extraQueryParams)),
    });
    return url.toString();
  }

  /**
   * Calculates a time delay based on exponential backoff and attempts connection
   * @private
   */
  _reconnect() {
    if (this._stopped) {
      return;
    }

    if (this._firstFailureAt === undefined) {
      this._firstFailureAt = Date.now();
    }

    const reconnectionDelay = getReconnectionDelay(
      this._reconnectionCount,
      this._reconnectionDelay,
    );
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = undefined;
      // Not awaited, and there is nothing to catch: _openConnection routes both
      // a token failure and a socket construction failure through
      // _handleConnectFailure, so the promise it returns always resolves.
      this._openConnection();
    }, reconnectionDelay);
    this._reconnectionCount++;
    this._isReconnection = true;
  }

  /**
   * Invokes every registered close callback.
   * @param {Object} detail - Passed through to each callback
   * @private
   */
  _notifyCloseCallbacks(detail) {
    if (!Object.keys(this._closeCallbacks).length) {
      return;
    }
    Object.values(this._closeCallbacks).forEach(cb => cb(detail));
  }

  /**
   * Reports that the client has stopped trying to reconnect. Silence here is
   * the defect the cap used to cause, so this is always captured.
   *
   * The context answers the questions that otherwise need a join against
   * backend logs: whether the machine believed it had a network, whether anyone
   * was looking at the tab, how long the run of failures lasted, which of the
   * three paths gave up, and what the server said on the last close. A fast 401
   * loop and a real network outage are indistinguishable without the last two.
   * @param {Object} extra - Additional Sentry context
   * @private
   */
  _reportExhausted(extra = {}) {
    captureExceptionWithScope(
      new Error('Hermes reconnection attempts exhausted'),
      {
        client: this._client,
        maxReconnections: this._maxReconnections,
        online: _onlineHint(),
        visibilityState: _visibilityState(),
        msSinceFirstFailure:
          this._firstFailureAt === undefined
            ? undefined
            : Date.now() - this._firstFailureAt,
        attemptCount: this._reconnectionCount,
        ...this._lastCloseEvent,
        ...extra,
      },
      ['hermes-reconnect-exhausted'],
      { hermesClient: this._client },
    );
  }

  /**
   * Handles a connection attempt that never produced a socket, which today
   * means the access token could not be resolved. Reported and retried under
   * the same cap as any other failure — the chain must not end in silence.
   *
   * A non-retryable token error (no refresh token, nothing to retry with) is
   * treated as immediately exhausted rather than queued for another attempt:
   * every retry would fail identically, and the dispatcher needs the "live
   * updates stopped" notice now instead of after burning the reconnection
   * budget on attempts that cannot succeed.
   * @param {*} error - Whatever the token provider rejected with
   * @private
   */
  _handleConnectFailure(error) {
    const reconnectionCount = this._reconnectionCount;
    const exhausted = isNonRetryableError(error) || this._isExhausted();

    // Avoid `instanceof Error`: it is false across realms, which bites in jsdom.
    const reported =
      error && error.message
        ? error
        : new Error(`Hermes access token unavailable: ${String(error)}`);

    // First failure only. Every attempt would put 10 near-identical events per
    // client on a single outage, and _reportExhausted already covers the far
    // end of the chain, so the ones in between add volume and no information.
    if (reconnectionCount === 0) {
      captureExceptionWithScope(
        reported,
        {
          client: this._client,
          reconnectionCount,
          exhausted,
        },
        ['hermes-access-token-unavailable'],
        { hermesClient: this._client },
      );
    }

    // Same first-close grace as an unintended socket close: a token blip that
    // the next attempt recovers from should not flash a notice. A
    // non-retryable error skips the grace period via `exhausted` above —
    // there is no next attempt that could recover.
    if (exhausted || reconnectionCount >= 1) {
      this._notifyCloseCallbacks({
        reconnectionCount,
        exhausted,
        stale: false,
      });
    }

    if (exhausted) {
      this._reportExhausted({
        tokenUnavailable: true,
        exhaustedVia: isNonRetryableError(error)
          ? 'nonRetryableTokenError'
          : 'tokenUnavailable',
      });
      return;
    }

    this._reconnect();
  }

  /**
   * Starts the staleness watchdog for the current connection.
   * @private
   */
  _startStaleWatchdog() {
    this._stopStaleWatchdog();
    this._lastInboundAt = Date.now();
    this._probeBufferedAmount = undefined;
    this._staleTimer = setInterval(
      () => this._checkStaleness(),
      this._staleCheckInterval,
    );
  }

  /**
   * Stops the staleness watchdog.
   * @private
   */
  _stopStaleWatchdog() {
    if (this._staleTimer !== undefined) {
      clearInterval(this._staleTimer);
      this._staleTimer = undefined;
    }
    this._probeBufferedAmount = undefined;
  }

  /**
   * One watchdog tick. A half-open socket never fires `onclose`, and a
   * `send()` on one buffers instead of throwing, so an outstanding probe that
   * the browser cannot drain is the signal that the socket is dead.
   * @private
   */
  _checkStaleness() {
    const connection = this._connection;
    if (
      !connection ||
      connection.closing ||
      connection.readyState !== _WS_STATE_OPEN
    ) {
      return;
    }

    if (this._probeBufferedAmount !== undefined) {
      const buffered = connection.bufferedAmount;
      if (buffered > 0 && buffered >= this._probeBufferedAmount) {
        this._handleStaleConnection(buffered);
        return;
      }
      // The probe drained, so the socket is alive despite the quiet. Treat the
      // drain as inbound activity, otherwise a quiet night probes every tick.
      this._probeBufferedAmount = undefined;
      this._lastInboundAt = Date.now();
      return;
    }

    if (Date.now() - this._lastInboundAt < this._staleInboundLimit) {
      return;
    }

    this._sendProbe(connection);
  }

  /**
   * Sends the inert probe frame and records the buffer depth to compare against
   * on the next tick.
   * @param {WebSocket} connection - The connection to probe
   * @private
   */
  _sendProbe(connection) {
    try {
      connection.send(JSON.stringify({ type: _PROBE_MESSAGE_TYPE }));
      this._probeBufferedAmount = connection.bufferedAmount;
    } catch (e) {
      // send() throwing means the socket is already unusable.
      this._handleStaleConnection(undefined);
    }
  }

  /**
   * Handles a connection that stopped delivering data without closing.
   * @param {number|undefined} bufferedAmount - Undrained bytes, when known
   * @private
   */
  _handleStaleConnection(bufferedAmount) {
    this._stopStaleWatchdog();

    const reconnectionCount = this._reconnectionCount;
    const exhausted = this._isExhausted();

    captureExceptionWithScope(
      new Error('Hermes connection went stale without closing'),
      {
        client: this._client,
        bufferedAmount,
        reconnectionCount,
        exhausted,
        staleInboundLimit: this._staleInboundLimit,
      },
      ['hermes-stale-connection'],
      { hermesClient: this._client },
    );

    this._notifyCloseCallbacks({
      reconnectionCount,
      exhausted,
      stale: true,
    });

    if (exhausted) {
      this._reportExhausted({ stale: true, exhaustedVia: 'stale' });
      return;
    }

    // _openConnection closes the dead socket before opening the replacement.
    this._reconnect();
  }

  /**
   * Opens WebSocket connection to Hermes and adds onmessage and onclose
   * handler. Resolves the access token first, so a socket is never opened with
   * a token this client has been holding since some earlier moment.
   * @returns {Promise<void>}
   * @private
   */
  async _openConnection() {
    if (this._stopped) {
      return;
    }

    this._addWakeListeners();

    // A token may take a round trip to obtain. Without this guard, every
    // subscribe() during that window would start a competing socket.
    if (this._opening) {
      return;
    }
    this._opening = true;

    let accessToken;
    try {
      accessToken = await this._getAccessToken();
    } catch (error) {
      this._opening = false;
      this._handleConnectFailure(error);
      return;
    }

    // closeConnection() during the token round trip found no socket to close,
    // so it recorded the intent here instead. Opening now would strand a socket
    // that nothing holds a reference to and nothing will ever close.
    if (this._stopped) {
      this._opening = false;
      return;
    }

    // If there is an existing connection close it before opening new one
    if (this._connection !== undefined) {
      this._closeSocket();
    }

    try {
      this._connection = new WebSocket(this._buildConnectionUrl(accessToken));
    } catch (error) {
      this._opening = false;
      this._handleConnectFailure(error);
      return;
    }
    this._opening = false;

    // Set local var to setup callbacks scoped to this connection
    const connection = this._connection;

    // Attach onclose handler for reconnection logic for unexpected closures
    connection.closing = false;
    connection.onclose = event => {
      this._stopStaleWatchdog();

      // Kept for the exhaustion report. The close code is what tells a 401 loop
      // apart from lost connectivity after the fact.
      this._lastCloseEvent = {
        lastCloseCode: event && event.code,
        lastCloseWasClean: event && event.wasClean,
        lastCloseReason: event && event.reason,
      };

      const intentional = connection.closing;
      const exhausted = !intentional && this._isExhausted();

      // Notify close subscribers for unintended closures (e.g. ALI lookback).
      // `reconnectionCount` is 0 on the initial disconnect; a value > 0 means a
      // prior reconnect attempt failed to reopen the socket. `exhausted` marks
      // the close after which this client stops trying.
      //
      // The first close stays quiet: backoff floors at 250ms, so a rolling
      // restart of the server would otherwise flash a notice and withdraw it a
      // quarter second later. A second close means a reconnect has already
      // failed, which is worth telling the dispatcher about. Exhaustion always
      // reports, since nothing follows it.
      if (!intentional && (exhausted || this._reconnectionCount >= 1)) {
        this._notifyCloseCallbacks({
          reconnectionCount: this._reconnectionCount,
          exhausted,
          stale: false,
        });
      }

      if (exhausted) {
        this._reportExhausted({
          reconnectionCount: this._reconnectionCount,
          exhaustedVia: 'close',
        });
      }

      // Reopen connection if it's not meant to be closing or set closed
      // using the exponential backoff strategy and configured max reconnections.
      // The replacement connection resolves its own token, so there is nothing
      // to refresh here.
      if (!intentional && !exhausted) {
        this._reconnect();
      } else {
        connection.closing = false;
      }
    };

    connection.onopen = () => {
      const hasReconnected = this._isReconnection;
      this._isReconnection = false;

      // Attach onopen handler to reset reconnection counter on successful connection
      this._resetReconnectionBudget();
      this._startStaleWatchdog();

      // Send subscription messages for subscribe calls made while opening
      this._topics.forEach(topic => {
        return this._sendSubscriptionMessage(
          topic.topic,
          _SUBSCRIPTION_ACTION_SUBSCRIBE,
          topic.data,
        );
      });

      if (hasReconnected && Object.keys(this._reconnectCallbacks).length) {
        Object.values(this._reconnectCallbacks).forEach(cb => {
          cb();
        });
      }
    };

    // If a callback from onMessage attach it to the WebSocket listener
    connection.onmessage = event => {
      this._lastInboundAt = Date.now();
      this._probeBufferedAmount = undefined;

      // Parse JSON if possible otherwise return raw string
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (e) {
        message = event.data;
      }

      if (this._callback) {
        const callback = this._callback;
        // Send message to the callback provided by user
        callback(message);
      }

      // Send message to any additional callbacks provided by user
      if (Object.keys(this._callbacks).length) {
        Object.values(this._callbacks).forEach(cb => {
          cb(message);
        });
      }
    };
  }

  /**
   * Sends a JSON message to Hermes with subscription instructions
   * @param {String} topic - The topic to subscribe/unsubscribe to/from
   * @param {String} action - 'subscribe' or 'unsubscribe'
   * @param {Object} data - Optional data to send with the subscription message
   * @private
   */
  _sendSubscriptionMessage(
    topic = _requiredParameter(),
    action = _requiredParameter(),
    data,
  ) {
    this.sendMessage({
      type: _SUBSCRIPTION_ACTION,
      body: {
        action: action,
        topic: topic,
        arguments: { ...data },
      },
    });
  }

  /**
   * Closes the current socket without ending the client, so a replacement can
   * take its place.
   * @private
   */
  _closeSocket() {
    this._stopStaleWatchdog();

    // Set closing flag so we know it was intentional and close the connection
    if (this._connection !== undefined) {
      this._connection.closing = true;
      this._connection.close();
    }
  }

  /**
   * Closes the connection to Hermes cleanly and stops the client. Unlike the
   * socket swap in _openConnection, nothing may reopen a socket afterwards:
   * a reconnect already scheduled is cancelled, and an attempt still awaiting
   * its token gives up once the token arrives. subscribe() revives the client.
   */
  closeConnection() {
    this._stopped = true;
    this._isReconnection = false;
    this._removeWakeListeners();

    if (this._reconnectTimer !== undefined) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }

    this._closeSocket();
  }

  /**
   * Set the handler to be invoked when a message is received
   * @param {function} callback - The callback function to be invoked
   */
  onMessage(callback) {
    if (!(callback instanceof Function)) {
      throw TypeError('Callback must be a function!');
    }
    this._callback = callback;
  }

  /**
   * Sends a message to the server
   * @param {string|object} message - The message to send to the server
   */
  sendMessage(message) {
    // Throw error if trying to send message with no connection
    if (
      !this._connection ||
      this._connection.closing ||
      this._connection.readyState === _WS_STATE_CLOSED
    ) {
      throw Error('Cannot send message without a connection!');
    }

    // Convert to JSON string if an object was provided
    if (typeof message === 'object') {
      message = JSON.stringify(message);
    }

    // Send the message over the WS connection
    this._connection.send(message);
  }

  /**
   * Sets additional callback handlers to be invoked when a message is received.
   * This assumes there is already an open connection
   * @param {Object} callbackMap
   * @param {Function} callbackMap[callbackName] - callbackName to map to
   * callback function
   */
  addOnMessageCallback(callbackMap) {
    if (typeof callbackMap === 'object' && !Array.isArray(callbackMap)) {
      Object.keys(callbackMap).forEach(callbackName => {
        if (!(callbackMap[callbackName] instanceof Function)) {
          throw TypeError(`${callbackName} must be a function!`);
        } else {
          // This will overwrite any existing callback handlers with the same name
          this._callbacks[callbackName] = callbackMap[callbackName];
        }
      });
    } else {
      throw TypeError('callbackMap must be an Object');
    }
  }

  /**
   * Removes specific onmessage callback handlers added through
   * addOnMessageCallback
   * @param {String} callbackFnName - name of the callback function that maps to
   * a specific key name in this._callbacks
   */
  removeOnMessageCallback(callbackFnName) {
    if (typeof callbackFnName !== 'string') {
      throw Error('callbackFnName must be a string');
    }

    delete this._callbacks[callbackFnName];
  }

  /**
   * Sets callback handlers to be invoked after a successful reconnect.
   * @param {Object} callbackMap
   * @param {Function} callbackMap - callback names mapped to callback functions
   */
  addOnReconnectCallback(callbackMap) {
    if (typeof callbackMap !== 'object' && Array.isArray(callbackMap)) {
      throw TypeError('callbackMap must be an Object');
    }
    Object.keys(callbackMap).forEach(callbackName => {
      if (!(callbackMap[callbackName] instanceof Function)) {
        throw TypeError(`${callbackName} must be a function!`);
      }
      this._reconnectCallbacks[callbackName] = callbackMap[callbackName];
    });
  }

  /**
   * Removes specific reconnect callback handlers.
   * @param {String} callbackFnName - name of the callback function that maps to
   * a specific key name in this._reconnectCallbacks
   */
  removeOnReconnectCallback(callbackFnName) {
    if (typeof callbackFnName !== 'string') {
      throw Error('callbackFnName must be a string');
    }

    delete this._reconnectCallbacks[callbackFnName];
  }

  /**
   * Sets callback handlers to be invoked when the connection closes
   * unexpectedly, or when the watchdog finds it dead without a close event.
   * Each callback receives `{ reconnectionCount, exhausted, stale }`:
   * `reconnectionCount` 0 is the initial disconnect and > 0 a failed reconnect
   * attempt, `exhausted` is true on the last notification this client will
   * send, and `stale` is true when the watchdog rather than `onclose` found it.
   * @param {Object} callbackMap - callback names mapped to callback functions
   */
  addOnCloseCallback(callbackMap) {
    if (typeof callbackMap === 'object' && !Array.isArray(callbackMap)) {
      Object.keys(callbackMap).forEach(callbackName => {
        if (!(callbackMap[callbackName] instanceof Function)) {
          throw TypeError(`${callbackName} must be a function!`);
        }
        this._closeCallbacks[callbackName] = callbackMap[callbackName];
      });
    } else {
      throw TypeError('callbackMap must be an Object');
    }
  }

  /**
   * Removes a specific close callback handler.
   * @param {String} callbackFnName - name of the callback to remove
   */
  removeOnCloseCallback(callbackFnName) {
    if (typeof callbackFnName !== 'string') {
      throw Error('callbackFnName must be a string');
    }

    delete this._closeCallbacks[callbackFnName];
  }

  /**
   * Returns whether the WebSocket connection is currently open.
   * @returns {boolean}
   */
  isConnected() {
    return !!this._connection && this._connection.readyState === _WS_STATE_OPEN;
  }

  /**
   * Subscribe to receive messages for a given topic
   * @param {String} topic - Topic to subscribe to messages for
   * @param {Object} data - Optional data to send with the subscription message
   * @throws {Error} topic must contain a value
   */
  async subscribe(topic = _requiredParameter(), data = undefined) {
    // Check if the topic is already subscribed before adding it to the list
    let alreadySubscribed = false;
    this._topics.forEach(t => {
      if (t.topic == topic) {
        if (!t.data && !data) {
          // If the topic is already subscribed without data, but the new
          // subscription doesn't have data, then don't subscribe again.
          alreadySubscribed = true;
        } else if (!!t.data && !data) {
          // If the topic is already subscribed with data, but the new
          // subscription doesn't have data, then don't subscribe again.
          alreadySubscribed = true;
        } else if (isEqual(t.data, data)) {
          // If the topic is already subscribed with the same data, then
          // don't subscribe again.
          alreadySubscribed = true;
        }
      }
    });

    // Save the topic to the internal list of subscribed topics
    if (!alreadySubscribed) {
      this._topics.add({ topic: topic, data: data });
    }

    // Subscribe to the topic if a connection exists and is open
    if (this._connection && this._connection.readyState === _WS_STATE_OPEN) {
      // Make sure not to send duplicate subscription messages
      if (!alreadySubscribed) {
        this._sendSubscriptionMessage(
          topic,
          _SUBSCRIPTION_ACTION_SUBSCRIBE,
          data,
        );
      }
      return;
    }

    // A connection that is still opening will send every saved topic from its
    // onopen handler, so reopening here would only tear down a socket that is
    // about to work.
    if (
      this._connection &&
      this._connection.readyState === _WS_STATE_CONNECTING
    ) {
      return;
    }

    // Subscribing is a request for live data, so it revives a client that
    // closeConnection() stopped.
    this._stopped = false;

    // Open a new connection if one doesn't exist or is closed.
    // NOTE: At this point, the current topic is saved and will be
    // subscribed to when the connection is opened (see connection.onopen).
    // Awaiting is optional for callers: the topic above is recorded
    // synchronously and connection.onopen replays every saved topic.
    return this._openConnection();
  }

  /**
   * Unsubscribe from receiving messages for given topic
   * @param {String} topic - Topic to subscribe to messages for
   * @throws {Error} topic must contain a value
   */
  unsubscribe(topic = _requiredParameter()) {
    // Remove the topic from the list of subscribed topics
    this._topics.forEach(t => {
      if (t.topic === topic) {
        this._topics.delete(t);
      }
    });

    // Send message to unsubscribe from the topic if a connection exists
    if (this._connection && this._connection.readyState === _WS_STATE_OPEN) {
      this._sendSubscriptionMessage(topic, _SUBSCRIPTION_ACTION_UNSUBSCRIBE);
    }
  }

  /**
   * Unsubscribe from receiving messages on all topics
   */
  unsubscribeAll() {
    this._topics.forEach(topic => {
      this.unsubscribe(topic.topic);
    });
  }
}

export default HermesClientBase;
